import React, { useState, useMemo } from 'react';
import ReactJson from "react-json-view";

function RawJsonModal({ rawJson, onClose }) {
    const parsed = useMemo(() => {
        try { return JSON.parse(rawJson); }
        catch { return { error: "Invalid JSON", raw: rawJson }; }
    }, [rawJson]);

    const decoded = useMemo(() => decodeVSD(parsed), [parsed]);
    const [collapsed, setCollapsed] = useState(2);
    const [rawMode, setRawMode] = useState(false);
    const [searchText, setSearchText] = useState('');

    const formatted = useMemo(() => JSON.stringify(decoded, null, 2), [decoded]);

    // Filter JSON: search both field names AND values
    const { filtered, matchCount } = useMemo(() => {
        if (!searchText.trim()) return { filtered: decoded, matchCount: -1 };
        const result = filterJson(decoded, searchText.trim());
        const count = result ? countMatches(result) : 0;
        return { filtered: result || {}, matchCount: count };
    }, [decoded, searchText]);

    const hasResults = matchCount === -1 || matchCount > 0;
    const isSearching = searchText.trim().length > 0;

    const handleDownload = () => {
        const blob = new Blob([rawJson], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `raw-event-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
             onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-[900px] h-[700px] flex flex-col border border-gray-200"
                 onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="px-6 py-3 border-b border-gray-200 rounded-t-xl bg-gray-50 flex-shrink-0">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <span className="text-lg">📄</span>
                            <h3 className="text-sm font-semibold text-gray-700">Raw CDC Event</h3>
                            <span className="text-xs text-gray-400 bg-gray-200 rounded px-2 py-0.5">JSON</span>
                            {isSearching && (
                                <span className={`text-xs px-2 py-0.5 rounded ${hasResults ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                    {hasResults ? `${matchCount} matches` : 'No results'}
                                </span>
                            )}
                        </div>
                        <button onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full w-7 h-7 flex items-center justify-center text-lg leading-none">
                            &times;
                        </button>
                    </div>

                    {/* Toolbar */}
                    <div className="flex items-center gap-2">
                        <button onClick={() => setCollapsed(-1)}
                                className="text-xs px-2.5 py-1.5 bg-white border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100">Expand All</button>
                        <button onClick={() => setCollapsed(999)}
                                className="text-xs px-2.5 py-1.5 bg-white border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100">Collapse All</button>
                        <div className="w-px h-5 bg-gray-300" />
                        <button onClick={() => setRawMode(!rawMode)}
                                className={`text-xs px-2.5 py-1.5 border rounded-md ${rawMode ? 'bg-blue-100 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-100'}`}>
                            {rawMode ? 'Tree View' : 'Raw Text'}
                        </button>
                        <div className="w-px h-5 bg-gray-300" />
                        <button onClick={handleDownload}
                                className="text-xs px-2.5 py-1.5 bg-white border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100">
                            ⬇ Download
                        </button>
                        <div className="flex-1" />
                        <div className="relative">
                            <input type="text" value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                placeholder="Search fields or values..."
                                className="w-52 text-xs border border-gray-300 rounded-md px-3 py-1.5 pl-7 bg-white" />
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">🔍</span>
                        </div>
                    </div>
                </div>

                {/* JSON Content */}
                <div className="flex-1 overflow-auto" style={{ backgroundColor: '#1e1e2e' }}>
                    {!hasResults && isSearching ? (
                        <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                            <div className="text-center">
                                <div className="text-2xl mb-2">🔍</div>
                                <div>No matches for "<span className="text-gray-300">{searchText}</span>"</div>
                                <div className="text-xs text-gray-600 mt-1">Try a different keyword</div>
                            </div>
                        </div>
                    ) : rawMode ? (
                        <pre className="text-xs font-mono leading-relaxed p-4 m-0 whitespace-pre-wrap text-gray-300 select-text"
                             style={{ minHeight: '400px' }}>
                            {formatted}
                        </pre>
                    ) : (
                        <div className="p-4">
                            <ReactJson
                                src={isSearching ? filtered : decoded}
                                theme="monokai"
                                collapsed={isSearching ? false : (collapsed === -1 ? false : collapsed === 999 ? true : collapsed)}
                                enableClipboard={true}
                                displayDataTypes={false}
                                displayObjectSize={true}
                                sortKeys={true}
                                collapseStringsAfterLength={150}
                                name={null}
                                style={{
                                    fontFamily: "'Fira Code', 'Consolas', 'Courier New', monospace",
                                    fontSize: '12px',
                                    lineHeight: '1.6',
                                    backgroundColor: 'transparent'
                                }}
                                iconStyle="triangle"
                                indentWidth={3}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Filter JSON: show nodes where field name OR string value matches */
function filterJson(obj, search) {
    const q = search.toLowerCase();

    if (obj === null || obj === undefined) return undefined;
    if (typeof obj === 'string') {
        return obj.toLowerCase().includes(q) ? obj : undefined;
    }
    if (typeof obj === 'number' || typeof obj === 'boolean') {
        return String(obj).toLowerCase().includes(q) ? obj : undefined;
    }
    if (Array.isArray(obj)) {
        const mapped = obj.map(item => filterJson(item, q)).filter(m => m !== undefined);
        return mapped.length > 0 ? mapped : undefined;
    }

    const result = {};
    for (const [key, val] of Object.entries(obj)) {
        const keyMatch = key.toLowerCase().includes(q);
        const childFiltered = filterJson(val, q);
        const valMatch = childFiltered !== undefined;

        if (keyMatch || valMatch) {
            if (keyMatch && typeof val === 'object' && val !== null) {
                result[key] = childFiltered || `[${Array.isArray(val) ? val.length : Object.keys(val).length} fields]`;
            } else {
                result[key] = keyMatch ? val : childFiltered;
            }
        }
    }
    return Object.keys(result).length > 0 ? result : undefined;
}

/** Count total leaf matches in a filtered result */
function countMatches(obj) {
    if (obj === null || obj === undefined) return 0;
    if (typeof obj !== 'object') return 1;
    if (Array.isArray(obj)) return obj.reduce((sum, i) => sum + countMatches(i), 0);
    return Object.values(obj).reduce((sum, v) => sum + countMatches(v), 0);
}

/** Decode Debezium VariableScaleDecimal */
function decodeVSD(obj) {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;
    if (obj.scale !== undefined && obj.value !== undefined && typeof obj.value === 'string') {
        try {
            const binary = atob(obj.value);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
            const bigInt = BigInt('0x' + hex);
            const divisor = BigInt(10) ** BigInt(obj.scale);
            const intPart = bigInt / divisor;
            const fracPart = bigInt % divisor;
            return fracPart > 0n
                ? intPart.toString() + '.' + fracPart.toString().padStart(obj.scale, '0')
                : intPart.toString();
        } catch {
            return obj.value;
        }
    }
    if (Array.isArray(obj)) return obj.map(decodeVSD);
    const result = {};
    for (const [key, val] of Object.entries(obj)) result[key] = decodeVSD(val);
    return result;
}

export default RawJsonModal;
